/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import BlogTile from "@eisinc/App/Components/BlogTileComponent/BlogTile";
import loadBlogs from "@eisinc/App/Handlers/Data/Blogs/LoadBlogs";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getBlogsState } from "@eisinc/App/Store/Selectors/BlogsSelector";
import {
    CustomFieldDefinition,
    CustomSelectBrandsFieldDefinition,
    CustomSystemListFieldDefinition,
} from "@eisinc/CustomFieldDefinition";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { CategoriesFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brands = "brands",
    categories = "categories",
    solutions = "solutions",
    pageSize = "pageSize",
}

const mapStateToProps = (state: CustomAppState, props: any) => {
    const isLoading = getBlogsState(state)?.isLoading;
    const { pathname } = getLocation(state);
    const isCategoryPage = getCurrentPage(state).type === "CategoryDetailsPage";
    const isBrandDetailsPage = getCurrentPage(state).type === "BrandDetailsPage";

    let brandId = null;
    let categoryId = null;

    if (isBrandDetailsPage) {
        const brandPath = getSelectedBrandPath(state) || pathname;

        brandId = getBrandStateByPath(state, brandPath)?.value?.id;
    }

    if (isCategoryPage) {
        const categoryPath =
            getSelectedCategoryPath(state) || (pathname.toLowerCase().startsWith("/content/") ? "" : pathname);

        categoryId = getCatalogPageStateByPath(state, categoryPath).value?.categoryId;
    }

    const blogs = getBlogsState(state)?.blogs;
    const key = getDataViewKey({
        category: categoryId ? [categoryId] : props?.fields?.categories || [],
        brand: brandId ? [brandId] : props?.fields?.brands || [],
        solution: props?.fields?.solutions || [],
        pageSize: props?.fields?.pageSize,
    });

    return {
        isCategoryPage,
        isBrandDetailsPage,
        brandId,
        categoryId,
        blogs: blogs[key] || [],
        isLoading,
    };
};

const mapDispatchToProps = {
    loadBlogs,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categories]: CategoriesFieldDefinition;
        [fields.brands]: CustomSelectBrandsFieldDefinition;
        [fields.solutions]: CustomSystemListFieldDefinition;
        [fields.pageSize]: number;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BlogsWidgetStyleProps {
    title?: TypographyPresentationProps;
    blogsWrapper?: InjectableCss;
    spinnerWrapper?: InjectableCss;
}

export const blogsStyles: BlogsWidgetStyleProps = {
    title: {
        size: "32px",
        lineHeight: "40px",
        color: "text.main",
        css: css`
            margin-bottom: 28px;
        `,
    },
    blogsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 50px;
        `,
    },
    spinnerWrapper: {
        css: css`
            height: 200px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
};

type BlogWidgetProps = Props & WidgetProps;

const BlogPageWidget: React.FC<BlogWidgetProps> = ({
    fields,
    loadBlogs,
    isLoading,
    blogs,
    isCategoryPage,
    isBrandDetailsPage,
    brandId,
    categoryId,
}) => {
    React.useEffect(() => {
        if (isCategoryPage && categoryId) {
            loadBlogs({
                category: [categoryId!],
                brand: [],
                solution: [],
                pageSize: fields.pageSize,
            });
        } else if (isBrandDetailsPage && brandId) {
            loadBlogs({
                category: [],
                brand: [brandId!],
                solution: [],
                pageSize: fields.pageSize,
            });
        } else if (!isBrandDetailsPage && !isCategoryPage) {
            loadBlogs({
                category: fields.categories as any,
                brand: fields.brands as any,
                solution: fields.solutions as any,
                pageSize: fields.pageSize,
            });
        }
    }, [brandId, categoryId, fields]);

    if (!blogs && isLoading) {
        return (
            <StyledWrapper {...blogsStyles.spinnerWrapper}>
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    if (!isLoading && !blogs.length) {
        return null;
    }

    return (
        // Note: using section wrapper to ensure proper semantics for the widget
        <StyledWrapper as="section">
            {fields.title && (
                <Typography as="h2" {...blogsStyles.title}>
                    {fields.title}
                </Typography>
            )}
            <StyledWrapper {...blogsStyles.blogsWrapper}>
                {blogs.map((blog: any, index: number) => (
                    <React.Fragment key={`${blog.title}_${index}`}>
                        <BlogTile key={blog.title} blog={blog} />
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Common" as any,
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            fieldType: "General",
            defaultValue: "",
        },
        {
            name: fields.categories,
            displayName: "Select Category",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
        },
        {
            name: fields.brands,
            displayName: "Select Brand",
            editorTemplate: "CustomSelectBrandsField",
            defaultValue: [],
            fieldType: "General",
        },
        {
            name: fields.solutions,
            displayName: "Select Solution",
            editorTemplate: "SystemListMultiSelectDropDownField",
            systemListName: "Custom_Solutions",
            fieldType: "General",
            defaultValue: [] as any,
        },
        {
            name: fields.pageSize,
            displayName: "Number Of Blogs",
            editorTemplate: "IntegerField",
            defaultValue: 4,
            fieldType: "General",
            placeholder: "ُEnter the max number of blogs",
            min: 1,
        },
    ] as any,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(BlogPageWidget),
    definition,
};

export default widgetModule;
